package dev.mobilearchitecture.web.components.sections

import androidx.compose.runtime.*
import com.varabyte.kobweb.browser.dom.ElementTarget
import kotlinx.browser.window
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.icons.CloseIcon
import com.varabyte.kobweb.silk.components.icons.HamburgerIcon
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.overlay.Overlay
import com.varabyte.kobweb.silk.components.overlay.OverlayVars
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.breakpoint.displayUntil
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.compose.ui.styleModifier
import org.jetbrains.compose.web.css.*
import dev.mobilearchitecture.web.components.widgets.IconButton
import dev.mobilearchitecture.web.toSitePalette

import com.varabyte.kobweb.compose.css.boxShadow
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.silk.components.icons.fa.FaAndroid
import com.varabyte.kobweb.silk.components.icons.fa.FaApple

val NavHeaderStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .padding(topBottom = 1.cssRem, leftRight = 2.cssRem)
            .position(org.jetbrains.compose.web.css.Position.Fixed)
            .top(0.px)
            .styleModifier { property("z-index", "10") }
    }
}

val LogoStyle = CssStyle {
    base {
        Modifier
            .fontSize(1.5.cssRem)
            .fontWeight(FontWeight.Bold)
            .textDecorationLine(TextDecorationLine.None)
    }
}

@Composable
private fun NavLink(path: String, text: String) {
    Link(path, text, variant = UndecoratedLinkVariant.then(UncoloredLinkVariant))
}

@Composable
private fun MenuItems(onLinkClick: () -> Unit = {}) {
    val createOnClick: (String) -> (() -> Unit) = { path ->
        {
            onLinkClick()
            window.location.hash = path
        }
    }
    Link("#hero-vanta-bg", "Home", variant = UndecoratedLinkVariant.then(UncoloredLinkVariant), modifier = Modifier.color(Color.rgb(0xF8FAFC)).onClick { createOnClick("#hero-vanta-bg")() })
    Link("#about", "About", variant = UndecoratedLinkVariant.then(UncoloredLinkVariant), modifier = Modifier.color(Color.rgb(0xF8FAFC)).onClick { createOnClick("#about")() })
    Link("#session", "How It Works", variant = UndecoratedLinkVariant.then(UncoloredLinkVariant), modifier = Modifier.color(
        Color.rgb(0xF8FAFC)).onClick { createOnClick("#session")() })
    Link("#topics", "Topics", variant = UndecoratedLinkVariant.then(UncoloredLinkVariant), modifier = Modifier.color(
        Color.rgb(0xF8FAFC)).onClick { createOnClick("#topics")() })
    Link("#faq", "FAQ", variant = UndecoratedLinkVariant.then(UncoloredLinkVariant), modifier = Modifier.color(Color.rgb(0xF8FAFC)).onClick { createOnClick("#faq")() })
    Link("#book", "Book a Session →", variant = UndecoratedLinkVariant.then(UncoloredLinkVariant), modifier = Modifier.color(
        Color.rgb(0xF8FAFC)).onClick { createOnClick("#book")() })
}

@Composable
private fun HamburgerButton(onClick: () -> Unit) {
    IconButton(onClick) {
        HamburgerIcon()
    }
}

@Composable
private fun CloseButton(onClick: () -> Unit) {
    IconButton(onClick) {
        CloseIcon()
    }
}

val SideMenuSlideInAnim = Keyframes {
    from {
        Modifier.translateX(100.percent)
    }

    to {
        Modifier
    }
}

// Note: When the user closes the side menu, we don't immediately stop rendering it (at which point it would disappear
// abruptly). Instead, we start animating it out and only stop rendering it when the animation is complete.
enum class SideMenuState {
    CLOSED,
    OPEN,
    CLOSING;

    fun close() = when (this) {
        CLOSED -> CLOSED
        OPEN -> CLOSING
        CLOSING -> CLOSING
    }
}

@Composable
fun NavHeader() {
    Row(NavHeaderStyle.toModifier().backgroundColor(Color.rgba(0, 0, 0, 0.7f)).styleModifier { property("backdrop-filter", "blur(10px)") }, verticalAlignment = Alignment.CenterVertically) {
        Row(
            modifier = LogoStyle.toModifier(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.px)
        ) {
            FaAndroid(modifier = Modifier.color(Color.rgb(0xF8FAFC)).size(32.px))
            FaApple(modifier = Modifier.color(Color.rgb(0xF8FAFC)).size(32.px))
        }

        Spacer()

        Row(Modifier.gap(1.5.cssRem).displayIfAtLeast(Breakpoint.MD), verticalAlignment = Alignment.CenterVertically) {
            MenuItems()
        }

        Row(
            Modifier
                .fontSize(1.5.cssRem)
                .gap(1.cssRem)
                .displayUntil(Breakpoint.MD),
            verticalAlignment = Alignment.CenterVertically
        ) {
            var menuState by remember { mutableStateOf(SideMenuState.CLOSED) }

            HamburgerButton(onClick =  {
                if (menuState == SideMenuState.CLOSED) {
                    menuState = SideMenuState.OPEN
                } else {
                    menuState = menuState.close()
                }
            })

            if (menuState != SideMenuState.CLOSED) {
                SideMenu(
                    menuState,
                    close = { menuState = menuState.close() },
                    onAnimationEnd = { if (menuState == SideMenuState.CLOSING) menuState = SideMenuState.CLOSED }
                )
            }
        }
    }
}

@Composable
private fun SideMenu(menuState: SideMenuState, close: () -> Unit, onAnimationEnd: () -> Unit) {
    Overlay(
        Modifier
            .setVariable(OverlayVars.BackgroundColor, Color.rgba(0, 0, 0, 0.8f))
            .onClick { close() }
    ) {
        key(menuState) { // Force recompute animation parameters when close button is clicked
            Column(
                Modifier
                    .fillMaxHeight()
                    .width(clamp(12.cssRem, 50.percent, 16.cssRem))
                    .align(Alignment.CenterEnd)
                    // Close button will appear roughly over the hamburger button, so the user can close
                    // things without moving their finger / cursor much.
                    .padding(top = 1.cssRem, leftRight = 1.cssRem)
                    .gap(1.5.cssRem)
                    .backgroundColor(Color.rgb(0x000000))
                    .animation(
                        SideMenuSlideInAnim.toAnimation(
                            duration = 200.ms,
                            timingFunction = if (menuState == SideMenuState.OPEN) AnimationTimingFunction.EaseOut else AnimationTimingFunction.EaseIn,
                            direction = if (menuState == SideMenuState.OPEN) AnimationDirection.Normal else AnimationDirection.Reverse,
                            fillMode = AnimationFillMode.Forwards
                        )
                    )
                    .borderRadius(topLeft = 2.cssRem)
                    .onClick { it.stopPropagation() }
                    .onAnimationEnd { onAnimationEnd() },
                horizontalAlignment = Alignment.End
            ) {
                Column(
                    Modifier
                        .padding(top = 3.cssRem, right = 0.75.cssRem)
                        .gap(1.5.cssRem)
                        .fontSize(1.4.cssRem)
                        .fillMaxWidth(),
                    horizontalAlignment = Alignment.Start
                ) {
                    MenuItems(onLinkClick = { close() })
                }
            }
        }
    }
}
